<script setup lang="ts">
import 'virtual:svg-icons-register';
import PartHeader from '@parts/part-header.vue';
import PartHeaderDesktop from '@parts/part-header-desktop.vue';
import PartNavigationBar from '@parts/part-navigation-bar.vue';
import ButtonCommunication from '@buttons/button-communication.vue';
import PartUserModals from '@parts/part-user-modals.vue';

import {useCommonStore} from '@store/common';
import {storeToRefs} from 'pinia';
import {computed, onBeforeMount, ref} from "vue";
import {initData} from "@tma.js/sdk-vue";
import {useI18n} from "vue-i18n";
import CoreLoader from "@/components/core/core-loader.vue";
import PartAppSidebar from '@parts/part-app-sidebar.vue';
import {useUserStore} from "@/store/user";
import {useVpnConnector} from "@composables/vpnConnector";
import router from "@/router";
import { useRoute } from 'vue-router';
import { useThemeStore } from '@/store/theme';
import isInTelegram from "@/utils/isInTelegram";
import {useAuthApi} from "@store/api/authApi";
import * as Sentry from '@sentry/browser';
import { useHelpers } from '@/composables/helpers';
import { ApiAuthError } from '@/composables/useApi';

const isMiniApp = isInTelegram();
const commonStore = useCommonStore();
const {mainMenu, communicationLink} = storeToRefs(commonStore);
const {t} = useI18n();

const vpnConnector = useVpnConnector();
const userStore = useUserStore();
const themeStore = useThemeStore();
const authApi = useAuthApi();
const isLoading = ref<boolean>(true);
const loaderText = ref<string>(t('common.loader.default'));

const helpers = useHelpers();
const route = useRoute();
const fields = computed(() => {
	return {
		loaderAuthText: t('common.loader.auth'),
		loaderSettingsText: t('common.loader.settings')
	}
});

//TODO Показывать BackButton в телеграм
/*watch(() => router.length, (value) => {
  console.log(value);
});*/

onBeforeMount(async () => {
	themeStore.init();
  //For running VPN app
  /*if (vpnConnector.isRouteRunVpn()) {
    document.documentElement.style.setProperty('--max-page-width', '100%');
    isLoading.value = false;
    return;
  }*/
	// console.log('show route params before layout mount', route.params);
	Sentry.addBreadcrumb({
		category: 'router',
		level: 'debug',
		message: 'Route params before layout mount',
		data: route.params,
	});
	try {
		loaderText.value = fields.value.loaderAuthText;

		if (isMiniApp) { // Login with Mini-App
			const initDataRaw = initData.raw() || "";
			const userId = initData.user()?.id || null;

			Sentry.addBreadcrumb({
				category: 'miniapp.auth',
				message: 'Is TG Mini-App enviroment, start proccess',
				level: 'info',
				data: {
					initDataRaw_type: helpers.getType(initDataRaw),
					initDataRaw_length: initDataRaw?.length,
					user_tg_id: userId
				}
			});

			await userStore.methods.tgMiniAppInit(initDataRaw);

			Sentry.addBreadcrumb({
				category: 'miniapp.auth',
				message: 'Done fetch user',
				level: 'info',
				data: {
					tn_type: helpers.getType(authApi.token),
					tn_length: authApi.token?.length,
					user_type: helpers.getType(userStore.user),
					user_id: userStore.user?.id
				}
			});

			loaderText.value = fields.value.loaderSettingsText;

			await commonStore.init();

			Sentry.addBreadcrumb({
				category: 'miniapp.auth',
				message: 'Init common store',
				level: 'info',
			});

			if (isMiniApp && initData.startParam() !== undefined) {
				switch (initData.startParam()) {
					case 'apps':
					case 'app':
						void router.push({path: '/settings/download_app/app'})
						break;
					case 'help':
						void router.push({path: '/help'})
						break;
					case 'pay':
						void router.push({path: '/payment'})
						break;
					case 'sub':
						//void router.push({path: '/account/subscribe'})
						void router.push({path: '/payment'})
						break;
					case 'connect':
						void router.push({path: '/settings/setup_app/key'})
						break;
					case 'ref':
					case 'invite':
						void router.push({path: '/invite'})
						break;
					case 'email':
						void router.push({path: '/account/email'})
						break;
					case 'account':
					case 'settings':
						void router.push({path: '/account'})
						break;
				}
			}
		} else if (authApi.methods.getToken() !== null) { // Fetching user by Bearer Token;
			await userStore.methods.set();

			loaderText.value = fields.value.loaderSettingsText;

			await commonStore.init();
		}
	} catch (e) {
		if (!isMiniApp && e instanceof ApiAuthError) {
			authApi.methods.clearToken();

			router.replace({path: '/login'});
		} else {
			Sentry.captureException(e, {
				tags: {
					block: 'layout.beforeMount',
					auth_flow: isMiniApp ? 'miniapp' : 'token',
				},
			});
			console.log(e)
		}
	} finally {
		isLoading.value = false;
	}
});
</script>

<template>
<div class="container" :class="{'container_miniapp': isMiniApp}">
    <part-header
		v-if="userStore.loggedIn && route.name !== 'Connect' && route.name !== 'AccountEmailVerified' && route.name !== 'Welcome' && !isMiniApp"
		class="container__header"
	/>
	<part-header-desktop
		v-if="route.name !== 'Connect' && route.name !== 'AccountEmailVerified' && route.name !== 'Welcome'"
		class="container__header-desktop"
	/>
	<part-app-sidebar
		v-if="userStore.loggedIn && route.name !== 'Login' && route.name !== 'Connect' && route.name !== 'AccountEmailVerified' && route.name !== 'Welcome'"
		:list="mainMenu"
		class="container__sidebar"
	/>
    <router-view v-if='!isLoading'/>
    <core-loader
        v-else
        absolute
        :title="loaderText"
    />
    <part-navigation-bar
        v-if="userStore.loggedIn && !vpnConnector.isRouteRunVpn() && (route.name !== 'Login' && route.name !== 'Connect' && route.name !== 'AccountEmailVerified' && route.name !== 'Welcome')"
        :list="mainMenu"
		class="container__navigation"
    >
    </part-navigation-bar>

	<button-communication
		v-if="route.name !== 'Welcome'"
		:url="communicationLink"
		class="container__communication"
	/>

	<part-user-modals />
</div>
</template>

<style lang="scss">
.container {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;

  &_miniapp {
	padding-top: 3rem;
  }

  @include min-desktop {
	flex-direction: row;
  }

  &__header {
	flex: 0 0 auto;
	@include min-desktop {
		display: none;
	}
  }
  &__header-desktop {
	display: none;
	position: fixed;
	right: 4rem;
	top: 4rem;
	@include min-desktop {
		display: flex;
	}
  }
  &__navigation {
	position: fixed;
	width: 100%;
	z-index: 20;
	bottom: 0;
	height: var(--navigation-bar-height);
	@include min-desktop {
		display: none;
	}
  }
  &__sidebar {
	position: fixed;
	left: 2rem;
	top: 2rem;
	bottom: 2rem;
	display: none;
	@include min-desktop {
		display: flex;
	}
  }
  &__communication {
	position: fixed;
	bottom: 4rem;
	right: 4rem;
	display: none;

	@include min-desktop {
		display: flex;
	}
  }
}

/**
 * Страница, у которой содержимое меняется на месте (вкладки на оплате).
 *
 * У .page стоит margin: auto, и во флекс-контейнере блок центрируется по
 * вертикали: стоит содержимому стать ниже, как шапка вместе с иконкой и
 * переключателем уезжает вниз. Прижимаем верх, низ по-прежнему свободный.
 */
.page.page_top {
	margin-top: 0;
}

.page {

	min-height: 0;
	display: flex;
	flex-direction: column;
	padding-bottom: calc(var(--navigation-bar-height) + 1rem);
	width: var(--max-page-width);
	max-width: 100%;
	margin: auto;

	@include min-desktop {
		flex: 0 1 auto;
		padding: var(--page-vertical-padding) 0 calc(var(--page-vertical-padding) * 2);
	}
}
</style>
